import type { DemoStation, Targeting } from '@/types/demo';
import { getFamilyFromSku } from '@/types/demo';

/**
 * Which stations a targeting spec resolves to.
 *
 * Mirrors the SQL station_matches_targeting() used by
 * get_station_active_playlist / get_station_active_campaign: within a row the
 * selections are OR, across rows they are AND, and an empty spec matches every
 * station. Keep the two in step — this one only drives the dialog preview, but
 * a preview that disagrees with the server is worse than no preview.
 */
export function getTargetedStationIds(
  targeting: Targeting,
  stationList: (DemoStation & { location: { name: string } | null })[],
  buildList: { id: string; sku: string }[] = [],
): Set<string> {
  const active = stationList.filter(s => s.status !== 'retiring');
  const t = targeting ?? {};
  const hasAny = !!(t.locations?.length || t.families?.length || t.builds?.length || t.stations?.length);
  if (!hasAny) return new Set(active.map(s => s.id)); // empty = all

  return new Set(active.filter(s => {
    if (t.locations?.length && !t.locations.includes(s.location_id ?? '')) return false;
    if (t.stations?.length && !t.stations.includes(s.id)) return false;
    if (t.builds?.length && !t.builds.includes(s.build_id ?? '')) return false;
    if (t.families?.length) {
      const b = buildList.find(b => b.id === s.build_id);
      const fam = b ? getFamilyFromSku(b.sku) : null;
      if (!fam || !t.families.includes(fam)) return false;
    }
    return true;
  }).map(s => s.id));
}
